import React, { useState, useMemo, useEffect } from 'react';
import { DoughRecordCard } from './DoughRecordCard';
import { DoughBatch, DoughIngredientInput } from '../../../types/doughBatch';
import { DoughBatchIngredient } from '../../../types/doughBatchIngredient';
import { createBlankDoughBatch } from '../../../store/slices/doughBatchSlice';
import { useAppSelector } from '../../../store/hooks';
import { groupItemsBySection } from '../../../utils/haccpSectionUtils';
import { HaccpSectionHeader } from '../../../components/haccp/HaccpSectionHeader';
import { matchesSearchQuery } from '../../../utils/searchUtils';

interface DoughRecordListProps {
  records: DoughBatch[];
  onAdd: (record: DoughBatch, ingredients: DoughIngredientInput[]) => void;
  onUpdate: (record: DoughBatch) => void;
  onDelete: (recordId: string) => void;
  onTimesProducedChange?: (id: string, timesProduced: number) => void;
}

export const DoughRecordList: React.FC<DoughRecordListProps> = ({ records, onAdd, onUpdate, onDelete, onTimesProducedChange }) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newRecord, setNewRecord] = useState<DoughBatch>(createBlankDoughBatch());
  const [searchTerm, setSearchTerm] = useState('');

  // Get all dough batch ingredients from Redux state
  const allIngredients = useAppSelector(state => state.doughBatchIngredients.ingredients);

  // Filter records based on search
  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records;
    return records.filter(record => matchesSearchQuery(record, searchTerm));
  }, [records, searchTerm]);

  // Group records into sections by date
  const sections = useMemo(
    () => groupItemsBySection(filteredRecords, (record) => record.dateOfProduction),
    [filteredRecords]
  );

  // Track expanded/collapsed state for each section (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    for (const section of sections) {
      state[section.key] = false;
    }
    return state;
  });

  // Update expanded state when sections change (e.g., new month appears)
  useEffect(() => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      for (const section of sections) {
        if (!(section.key in newState)) {
          newState[section.key] = false;
          hasChanges = true;
        }
      }
      return hasChanges ? newState : prev;
    });
  }, [sections]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Helper to increment batch number (e.g., D-20260116-1 → D-20260116-2)
  const incrementBatchNumber = (batchNumber: string): string => {
    const match = batchNumber.match(/^(.+-)(\d+)$/);
    if (match) {
      const prefix = match[1];
      const num = parseInt(match[2], 10) + 1;
      return `${prefix}${num}`;
    }
    // Fallback: append -2 if no number suffix found
    return `${batchNumber}-2`;
  };

  // Handle duplicating a dough batch
  const handleDuplicate = (sourceBatch: DoughBatch) => {
    // Get ingredients for the source batch
    const sourceIngredients = allIngredients.filter(
      (ing: DoughBatchIngredient) => ing.doughBatchId === sourceBatch.id
    );

    // Group ingredients by name and collect lot numbers
    const ingredientMap = new Map<string, DoughIngredientInput>();
    sourceIngredients.forEach((ing: DoughBatchIngredient) => {
      const existing = ingredientMap.get(ing.ingredientName);
      if (existing) {
        // Add lot number to existing ingredient if not null
        if (ing.lotNumber) {
          existing.lotNumbers.push(ing.lotNumber);
        }
      } else {
        // Create new ingredient entry
        ingredientMap.set(ing.ingredientName, {
          ingredientName: ing.ingredientName,
          lotNumbers: ing.lotNumber ? [ing.lotNumber] : [],
          quantity: ing.quantity ?? undefined,
          unit: ing.unit ?? undefined
        });
      }
    });

    const mappedIngredients = Array.from(ingredientMap.values());

    // Create new batch with incremented batch number and today's date
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const newBatch = createBlankDoughBatch(sourceBatch.productName);
    newBatch.dateOfProduction = today;
    newBatch.batchNumber = incrementBatchNumber(sourceBatch.batchNumber);
    newBatch.preparedBy = sourceBatch.preparedBy;
    newBatch.verifiedBy = sourceBatch.verifiedBy;

    // Save the duplicated batch
    onAdd(newBatch, mappedIngredients);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewRecord(createBlankDoughBatch());
  };

  const handleSaveNew = (record: DoughBatch, ingredients: DoughIngredientInput[]) => {
    // Validate required fields
    if (!record.productName || !record.preparedBy || !record.verifiedBy) {
      alert('Product Name, Prepared By, and Verified By are required fields.');
      return;
    }

    onAdd({
      ...record,
      updatedAt: new Date().toISOString()
    }, ingredients);
    setIsAddingNew(false);
    setNewRecord(createBlankDoughBatch());
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewRecord(createBlankDoughBatch());
  };

  const handleEditToggle = (recordId: string) => {
    if (editingId === recordId) {
      setEditingId(null);
    } else {
      setEditingId(recordId);
    }
  };

  const handleUpdate = (record: DoughBatch) => {
    // Validate required fields
    if (!record.productName || !record.preparedBy || !record.verifiedBy) {
      alert('Product Name, Prepared By, and Verified By are required fields.');
      return;
    }

    onUpdate({
      ...record,
      updatedAt: new Date().toISOString()
    });
    setEditingId(null);
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  return (
    <div>
      {/* Add New Dough Button */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={handleAddNew}
          disabled={isAddingNew || editingId !== null}
          style={{
            ...buttonStyle,
            backgroundColor: isAddingNew || editingId !== null ? '#cccccc' : '#4CAF50',
            color: 'white',
            cursor: isAddingNew || editingId !== null ? 'not-allowed' : 'pointer'
          }}
          onMouseEnter={e => {
            if (!isAddingNew && editingId === null) {
              e.currentTarget.style.backgroundColor = '#45a049';
            }
          }}
          onMouseLeave={e => {
            if (!isAddingNew && editingId === null) {
              e.currentTarget.style.backgroundColor = '#4CAF50';
            }
          }}
        >
          + Add New Dough
        </button>
      </div>

      {/* Search */}
      {!isAddingNew && !editingId && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Search dough records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box' as const
            }}
          />
        </div>
      )}

      {/* New Dough Record Card */}
      {isAddingNew && (
        <DoughRecordCard
          record={newRecord}
          isEditMode={true}
          onUpdate={() => {}}
          onSaveNew={handleSaveNew}
          onDelete={handleCancelNew}
          onEditToggle={handleCancelNew}
        />
      )}

      {/* Existing Dough Records grouped by section */}
      {records.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          <p style={{ margin: 0, fontSize: '1rem' }}>No dough records yet. Click "Add New Dough" to create one.</p>
        </div>
      )}

      {/* No search results */}
      {records.length > 0 && filteredRecords.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          <p style={{ margin: 0, fontSize: '1rem' }}>No dough records match your search for "{searchTerm}".</p>
        </div>
      )}

      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: '16px' }}>
          <HaccpSectionHeader
            label={section.label}
            itemCount={section.items.length}
            isExpanded={expandedSections[section.key] ?? !section.isTrialSection}
            isTrialSection={section.isTrialSection}
            onToggle={() => toggleSection(section.key)}
          />

          {(expandedSections[section.key] ?? !section.isTrialSection) && (
            <div>
              {section.items.map((record) => (
                <DoughRecordCard
                  key={record.id}
                  record={record}
                  isEditMode={editingId === record.id}
                  onUpdate={handleUpdate}
                  onDelete={() => onDelete(record.id)}
                  onEditToggle={() => handleEditToggle(record.id)}
                  onDuplicate={() => handleDuplicate(record)}
                  onTimesProducedChange={onTimesProducedChange}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
